import type { ReactNode } from 'react';
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useFirstRunHint } from './useFirstRunHint';

const useStyles = makeStyles({
  target: {
    minWidth: 0,
    width: '100%',
    height: '100%',
    display: 'block',
  },
  surface: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    maxWidth: '260px',
    padding: tokens.spacingVerticalM,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
});

export type FirstRunHintProps = {
  id: string;
  body: string;
  dismissLabel?: string;
  children: (highlighted: boolean) => ReactNode;
};

export const FirstRunHint = ({
  id,
  body,
  dismissLabel = 'Close',
  children,
}: FirstRunHintProps) => {
  const styles = useStyles();
  const { open, dismiss } = useFirstRunHint(id);
  const highlighted = open;

  if (!open) {
    return <>{children(false)}</>;
  }

  return (
    <Popover
      open
      onOpenChange={(_, data) => {
        if (!data.open) dismiss();
      }}
      positioning="above"
      withArrow
      trapFocus={false}
      unstable_disableAutoFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        <div className={styles.target} onClick={dismiss}>
          {children(highlighted)}
        </div>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <Text className={styles.body}>{body}</Text>
        <Button
          appearance="transparent"
          size="small"
          icon={<DismissRegular />}
          aria-label={dismissLabel}
          onClick={dismiss}
        />
      </PopoverSurface>
    </Popover>
  );
};
